import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Menu from "components/common/Menu";
import { CommonStyles } from "styles/CommonStyles";
import { SideBarTabs } from "constants/tabs";
import { useTabActive } from "hook/useTabActive";
import {
  CarValuation,
  CollectJijiValuationObservations,
  CanonicalVehicleModel,
  CreateValuationObservation,
  GetCanonicalVehicleModels,
  GetValuationCalibration,
  GetValuationObservations,
  GetValuationPriceTrend,
  GetValuations,
  ImportValuationObservations,
  JijiCollectionResult,
  JijiPriceObservationPreview,
  PriceObservation,
  PriceTrendResult,
  PreviewJijiValuationObservation,
  RecordValuationOutcome,
  ResolveCanonicalVehicleModel,
  ReviewValuation,
  ValuationCalibration,
  TemporalValuationBacktest,
  MissingSaleOutcome,
  ValuationEvidenceCoverage,
} from "api/services/valuations";
import { Paths } from "router/Paths";
import PriceTrendChart from "components/valuation/PriceTrendChart";

const money = (value?: number) =>
  value ? `${Math.round(value).toLocaleString()} Birr` : "Not available";

const today = new Date().toISOString().slice(0, 10);

type ReviewDraft = {
  likely: string;
  low: string;
  high: string;
  notes: string;
  asking: string;
  sold: string;
  soldAt: string;
  verified: boolean;
};

const draftFor = (valuation: CarValuation): ReviewDraft => ({
  likely: String(valuation.staffLikelySalePrice || valuation.result.likelySalePrice || ""),
  low: String(valuation.staffLowPrice || valuation.result.lowPrice || ""),
  high: String(valuation.staffHighPrice || valuation.result.highPrice || ""),
  notes: valuation.staffNotes || "",
  asking: String(valuation.originalAskingPrice || valuation.result.suggestedAskingPrice || ""),
  sold: String(valuation.actualSalePrice || ""),
  soldAt: valuation.actualSoldAt?.slice(0, 10) || today,
  verified: Boolean(valuation.outcomeVerified),
});

const initialObservation = {
  makeName: "",
  model: "",
  year: "",
  mileage: "",
  fuelType: "Gas",
  transmissionType: "Automatic",
  location: "Addis Ababa",
  askingPrice: "",
  finalSalePrice: "",
  sourceType: "CompletedSale",
  sourceProvider: "BossCar",
  observedAt: today,
  verified: false,
  note: "",
};

function ValuationsPage() {
  useTabActive(SideBarTabs.Valuations.name);
  const navigate = useNavigate();
  const [tab, setTab] = useState<"requests" | "outcomes" | "market" | "models">("requests");
  const [valuations, setValuations] = useState<CarValuation[]>([]);
  const [observations, setObservations] = useState<PriceObservation[]>([]);
  const [vehicleModels, setVehicleModels] = useState<CanonicalVehicleModel[]>([]);
  const [calibration, setCalibration] = useState<ValuationCalibration | null>(null);
  const [temporalBacktest, setTemporalBacktest] = useState<TemporalValuationBacktest | null>(null);
  const [readyForAutomaticPricing, setReadyForAutomaticPricing] = useState(false);
  const [evidenceCoverage, setEvidenceCoverage] = useState<ValuationEvidenceCoverage | null>(null);
  const [missingSaleOutcomes, setMissingSaleOutcomes] = useState<MissingSaleOutcome[]>([]);
  const [mergeTargets, setMergeTargets] = useState<Record<string, string>>({});
  const [sourceProvider, setSourceProvider] = useState("");
  const [datasetText, setDatasetText] = useState("");
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [jijiUrl, setJijiUrl] = useState("");
  const [jijiRecord, setJijiRecord] = useState<JijiPriceObservationPreview | null>(null);
  const [jijiRightsConfirmed, setJijiRightsConfirmed] = useState(false);
  const [jijiBatchText, setJijiBatchText] = useState("");
  const [jijiBatchResults, setJijiBatchResults] = useState<JijiCollectionResult[]>([]);
  const [priceTrend, setPriceTrend] = useState<PriceTrendResult | null>(null);
  const [trendSource, setTrendSource] = useState("");
  const [trendDays, setTrendDays] = useState(90);
  const [trendLoading, setTrendLoading] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, ReviewDraft>>({});
  const [observation, setObservation] = useState(initialObservation);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [feedback, setFeedback] = useState("");

  const loadValuations = useCallback(async () => {
    setLoading(true);
    const response = await GetValuations({ search: search || undefined, status: status || undefined });
    setLoading(false);
    if (response.status !== 200) {
      setFeedback(response.message || "Could not load valuation requests.");
      return;
    }
    setValuations(response.valuations);
    setDrafts((current) => {
      const next = { ...current };
      response.valuations.forEach((item) => {
        if (!next[item._id]) next[item._id] = draftFor(item);
      });
      return next;
    });
  }, [search, status]);

  const loadCalibration = useCallback(async () => {
    const response = await GetValuationCalibration();
    if (response.status === 200) {
      setCalibration(response.calibration);
      setTemporalBacktest(response.temporalBacktest || null);
      setReadyForAutomaticPricing(response.readyForAutomaticPricing);
      setEvidenceCoverage(response.evidenceCoverage || null);
      setMissingSaleOutcomes(response.missingSaleOutcomes || []);
    }
  }, []);

  const loadObservations = useCallback(async () => {
    setLoading(true);
    const response = await GetValuationObservations({ search: search || undefined });
    setLoading(false);
    if (response.status !== 200) {
      setFeedback(response.message || "Could not load market observations.");
      return;
    }
    setObservations(response.observations);
  }, [search]);

  const loadPriceTrend = useCallback(async () => {
    setTrendLoading(true);
    const response = await GetValuationPriceTrend({
      search,
      sourceProvider: trendSource || undefined,
      days: trendDays,
    });
    setTrendLoading(false);
    if (response.status !== 200) {
      setFeedback(response.message || "Could not load the price trend.");
      return;
    }
    setPriceTrend(response.trend);
  }, [search, trendDays, trendSource]);

  const loadVehicleModels = useCallback(async () => {
    setLoading(true);
    const response = await GetCanonicalVehicleModels({
      search: search || undefined,
      limit: 500,
    });
    setLoading(false);
    if (response.status !== 200) {
      setFeedback(response.message || "Could not load vehicle model reviews.");
      return;
    }
    setVehicleModels(response.models);
    setMergeTargets((current) => {
      const next = { ...current };
      response.models.forEach((item) => {
        if (!next[item._id] && item.suggestedCanonicalModel?._id) {
          next[item._id] = item.suggestedCanonicalModel._id;
        }
      });
      return next;
    });
  }, [search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (tab === "requests") void loadValuations();
      else if (tab === "market") {
        void loadObservations();
        void loadPriceTrend();
      }
      else if (tab === "models") void loadVehicleModels();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [loadObservations, loadPriceTrend, loadValuations, loadVehicleModels, tab]);

  useEffect(() => {
    void loadCalibration();
  }, [loadCalibration]);

  const updateDraft = (id: string, field: keyof ReviewDraft, value: string | boolean) => {
    setDrafts((current) => ({
      ...current,
      [id]: { ...(current[id] || draftFor(valuations.find((item) => item._id === id)!)), [field]: value },
    }));
  };

  const saveReview = async (id: string) => {
    const draft = drafts[id];
    setSavingId(id);
    const response = await ReviewValuation(id, {
      staffLikelySalePrice: draft.likely,
      staffLowPrice: draft.low,
      staffHighPrice: draft.high,
      staffNotes: draft.notes,
      reviewStatus: "Reviewed",
    });
    setSavingId("");
    setFeedback(response.message || (response.status === 200 ? "Review saved." : "Review failed."));
    if (response.status === 200) void loadValuations();
  };

  const saveOutcome = async (id: string) => {
    const draft = drafts[id];
    if (!draft.sold) {
      setFeedback("Enter the actual final selling price.");
      return;
    }
    setSavingId(id);
    const response = await RecordValuationOutcome(id, {
      originalAskingPrice: draft.asking,
      actualSalePrice: draft.sold,
      actualSoldAt: draft.soldAt,
      outcomeVerified: draft.verified,
    });
    setSavingId("");
    setFeedback(response.message || (response.status === 200 ? "Sale recorded." : "Could not record sale."));
    if (response.status === 200) void loadValuations();
  };

  const addObservation = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingId("observation");
    const response = await CreateValuationObservation(observation);
    setSavingId("");
    setFeedback(response.message || (response.status === 201 ? "Observation saved." : "Could not save observation."));
    if (response.status === 201) {
      setObservation(initialObservation);
      void loadObservations();
    }
  };

  const resolveModel = async (
    item: CanonicalVehicleModel,
    action: "approve" | "merge" | "reject"
  ) => {
    const targetModel = mergeTargets[item._id];
    if (action === "merge" && !targetModel) {
      setFeedback("Select the model this name belongs to.");
      return;
    }
    setSavingId(item._id);
    const response = await ResolveCanonicalVehicleModel(item._id, {
      action,
      ...(targetModel ? { targetModel } : {}),
    });
    setSavingId("");
    setFeedback(
      response.message ||
        (response.status === 200 ? "Vehicle model review saved." : "Could not save model review.")
    );
    if (response.status === 200) void loadVehicleModels();
  };

  const importDataset = async (event: React.FormEvent) => {
    event.preventDefault();
    let records: Array<Record<string, unknown>>;
    try {
      const parsed = JSON.parse(datasetText);
      records = Array.isArray(parsed) ? parsed : parsed.records;
      if (!Array.isArray(records)) throw new Error();
    } catch {
      setFeedback("Paste a JSON array of market records or an object with a records array.");
      return;
    }
    setSavingId("dataset");
    const response = await ImportValuationObservations({
      sourceProvider,
      rightsConfirmed,
      records,
    });
    setSavingId("");
    if (response.status !== 200) {
      setFeedback(response.message || "Could not import the market dataset.");
      return;
    }
    setFeedback(
      `${response.summary.imported} imported, ${response.summary.updated} updated, ${response.summary.rejected} rejected.`
    );
    setDatasetText("");
    void loadObservations();
  };

  const previewJiji = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingId("jiji-preview");
    setJijiRecord(null);
    const response = await PreviewJijiValuationObservation(jijiUrl);
    setSavingId("");
    if (response.status !== 200 || !response.record) {
      setFeedback(response.message || "Could not read that Jiji car listing.");
      return;
    }
    setJijiRecord(response.record);
    setFeedback("Jiji price found. Review the vehicle details before saving it.");
  };

  const updateJijiRecord = (
    field: "makeName" | "model" | "year" | "mileage" | "fuelType" | "transmissionType" | "location" | "askingPrice",
    value: string
  ) => {
    setJijiRecord((current) => {
      if (!current) return current;
      const numericField = ["year", "mileage", "askingPrice"].includes(field);
      return {
        ...current,
        [field]: numericField ? Number(value.replace(/[^0-9]/g, "")) : value,
      } as JijiPriceObservationPreview;
    });
  };

  const saveJijiObservation = async () => {
    if (!jijiRecord || !jijiRightsConfirmed) {
      setFeedback("Review the Jiji record and confirm internal evidence use first.");
      return;
    }
    setSavingId("jiji-save");
    const response = await ImportValuationObservations({
      sourceProvider: "Jiji",
      rightsConfirmed: true,
      records: [jijiRecord],
    });
    setSavingId("");
    if (response.status !== 200 || response.summary.rejected) {
      setFeedback(
        response.errors?.[0]?.message || response.message || "Could not save the Jiji price."
      );
      return;
    }
    setFeedback(
      response.summary.imported
        ? "Jiji asking price added to market evidence."
        : "Jiji asking price checked again and its history was updated."
    );
    setJijiUrl("");
    setJijiRecord(null);
    setJijiRightsConfirmed(false);
    void loadObservations();
    void loadPriceTrend();
    void loadCalibration();
  };

  const collectJijiPrices = async () => {
    const urls = jijiBatchText
      .split(/\s+/)
      .map((value) => value.trim())
      .filter(Boolean);
    if (!urls.length) {
      setFeedback("Paste at least one individual Jiji car listing link.");
      return;
    }
    if (urls.length > 25) {
      setFeedback("Collect no more than 25 Jiji links in one batch.");
      return;
    }
    setSavingId("jiji-collect");
    setJijiBatchResults([]);
    const response = await CollectJijiValuationObservations(urls);
    setSavingId("");
    if (response.status !== 200 || !response.summary) {
      setFeedback(response.message || "Could not collect the Jiji prices.");
      return;
    }
    setJijiBatchResults(response.results);
    setFeedback(
      `${response.summary.imported} added, ${response.summary.updated} checked again, ${response.summary.rejected} rejected, ${response.summary.duplicates} duplicate links skipped.`
    );
    if (!response.summary.rejected) setJijiBatchText("");
    void loadObservations();
    void loadPriceTrend();
    void loadCalibration();
  };

  const pendingModelCount = vehicleModels.filter((item) => item.status === "Pending").length;
  const normalizedSearch = search.trim().toLowerCase();
  const visibleMissingSaleOutcomes = missingSaleOutcomes.filter((item) => {
    if (!normalizedSearch) return true;
    return [
      item.make?.name,
      item.model,
      item.tag,
      item.phone,
      item.owner?.name,
      item.owner?.phoneNumber,
    ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
  });

  return (
    <div className={CommonStyles.pageWrapper}>
      <Menu />
      <main className={ClassName.container}>
        <header className={ClassName.header}>
          <div>
            <p className={ClassName.eyebrow}>BOSSCAR MARKET INTELLIGENCE</p>
            <h1 className={ClassName.title}>Used-car valuations</h1>
            <p className={ClassName.subtitle}>Review selling-price estimates and capture the final prices that improve future results.</p>
          </div>
          <div className={ClassName.tabs}>
            <button className={tab === "requests" ? ClassName.activeTab : ClassName.tab} onClick={() => setTab("requests")}>Requests</button>
            <button className={tab === "outcomes" ? ClassName.activeTab : ClassName.tab} onClick={() => setTab("outcomes")}>Missing sales{missingSaleOutcomes.length ? ` (${missingSaleOutcomes.length})` : ""}</button>
            <button className={tab === "market" ? ClassName.activeTab : ClassName.tab} onClick={() => setTab("market")}>Market data</button>
            <button className={tab === "models" ? ClassName.activeTab : ClassName.tab} onClick={() => setTab("models")}>Model review{pendingModelCount ? ` (${pendingModelCount})` : ""}</button>
          </div>
        </header>

        <section className={ClassName.toolbar}>
          <input className={ClassName.input} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search make, model or phone" />
          {tab === "requests" ? (
            <select className={ClassName.input} value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All review states</option>
              <option>NeedsReview</option><option>AutoEstimated</option><option>Reviewed</option><option>Closed</option>
            </select>
          ) : null}
        </section>

        <div className={ClassName.coldStartNotice}>
          <strong>Cold-start mode:</strong> imported listings are advertised prices, not completed sales. Review every provisional estimate and record real final selling prices to improve the system.
        </div>

        <section className={ClassName.calibrationCard}>
          <div>
            <span>CALIBRATION SCORECARD</span>
            <strong>{temporalBacktest?.evaluatedCount || 0} leakage-free tests</strong>
            <small>{readyForAutomaticPricing ? `${temporalBacktest?.readyModelCount || 0} model(s) passed both model-level and global accuracy gates` : "Each model needs 30 historical tests, median error at most 10%, and 80% within 15%; the global scorecard must also pass"}</small>
          </div>
          <div><span>Verified outcomes</span><strong>{calibration?.verifiedCount || 0}</strong></div>
          <div><span>Backtest median error</span><strong>{temporalBacktest?.metrics.count ? `${temporalBacktest.metrics.medianAbsolutePercentageError}%` : "No data"}</strong></div>
          <div><span>Backtest within 10%</span><strong>{temporalBacktest?.metrics.count ? `${temporalBacktest.metrics.within10Percent}%` : "No data"}</strong></div>
          <div><span>Backtest within 15%</span><strong>{temporalBacktest?.metrics.count ? `${temporalBacktest.metrics.within15Percent}%` : "No data"}</strong></div>
        </section>

        <section className={ClassName.coverageCard}>
          <div><span>Usable model matches</span><strong>{evidenceCoverage?.accepted || 0} / {evidenceCoverage?.total || 0}</strong><small>Only accepted identities can become comparables.</small></div>
          <div><span>Names awaiting review</span><strong>{evidenceCoverage?.pending || 0}</strong><small>Resolve spelling and naming differences in Model review.</small></div>
          <div><span>Evidence sources</span><strong>{evidenceCoverage?.sourceCount || 0}</strong><small>Diverse independent sources reduce bias.</small></div>
          <div><span>Verified final sales</span><strong>{evidenceCoverage?.verifiedSales || 0}</strong><small>These are the strongest price observations.</small></div>
        </section>

        {feedback ? <div className={ClassName.feedback}>{feedback}</div> : null}

        {tab === "market" ? (
          <>
          <section className={ClassName.trendCard}>
            <div className={ClassName.trendHeader}>
              <div>
                <span>PRICE HISTORY</span>
                <h2>{search.trim() ? `${search.trim()} price over time` : "Search a vehicle to see its price trend"}</h2>
                <p>Red is the median advertised price. Green is the median final sale price verified by BossCar.</p>
              </div>
              <div className={ClassName.trendFilters}>
                <select className={ClassName.input} value={trendSource} onChange={(event) => setTrendSource(event.target.value)}>
                  <option value="">All sources</option>
                  {(priceTrend?.availableSources || []).map((source) => <option key={source}>{source}</option>)}
                </select>
                <select className={ClassName.input} value={trendDays} onChange={(event) => setTrendDays(Number(event.target.value))}>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={180}>6 months</option>
                  <option value={365}>1 year</option>
                  <option value={730}>2 years</option>
                </select>
              </div>
            </div>
            {!search.trim() ? (
              <div className={ClassName.trendEmpty}>Use the search box above, for example <strong>Toyota Corolla</strong>. Combining unrelated models would create a misleading graph.</div>
            ) : trendLoading ? (
              <div className={ClassName.trendEmpty}>Loading price history...</div>
            ) : priceTrend?.points.length ? (
              <>
                <div className={ClassName.trendSummary}>
                  <div><span>Latest advertised median</span><strong>{money(priceTrend.summary.latestAskingMedian)}</strong></div>
                  <div><span>Change in this period</span><strong className={priceTrend.summary.askingChangePercent > 0 ? "text-primary" : priceTrend.summary.askingChangePercent < 0 ? "text-emerald-600" : ""}>{priceTrend.summary.askingChangePercent > 0 ? "+" : ""}{priceTrend.summary.askingChangePercent}%</strong></div>
                  <div><span>Listings tracked</span><strong>{priceTrend.summary.observationCount}</strong></div>
                  <div><span>Independent sources</span><strong>{priceTrend.summary.sourceCount}</strong></div>
                </div>
                <div className={ClassName.trendLegend}><span><i className="bg-primary" />Advertised median</span><span><i className="bg-emerald-600" />Verified sale median</span></div>
                <PriceTrendChart points={priceTrend.points} />
              </>
            ) : (
              <div className={ClassName.trendEmpty}>No dated prices match this vehicle and period yet. Add evidence below; the graph will grow from each observation date.</div>
            )}
          </section>

          <section className={ClassName.jijiCard}>
            <div className={ClassName.sectionHeading}>
              <div><span className={ClassName.jijiLabel}>JIJI PRICE EVIDENCE</span><h2>Collect used-car asking prices</h2><p>Paste individual car links, one per line. BossCar stores only vehicle facts, asking price, observation date, and price history for internal valuation. It does not create posts or retain photos, descriptions, or seller details.</p></div>
            </div>
            <textarea className={ClassName.jijiBatchText} value={jijiBatchText} onChange={(event) => setJijiBatchText(event.target.value)} placeholder={"https://jiji.com.et/.../cars/...html\nhttps://jiji.com.et/.../cars/...html"} />
            <div className={ClassName.jijiBatchAction}>
              <small>Up to 25 individual listing links per batch. Search and category pages are rejected.</small>
              <button type="button" className={ClassName.secondaryButton} disabled={savingId === "jiji-collect"} onClick={collectJijiPrices}>{savingId === "jiji-collect" ? "Collecting..." : "Collect prices"}</button>
            </div>
            {jijiBatchResults.length ? (
              <div className={ClassName.jijiBatchResults}>
                {jijiBatchResults.map((result) => (
                  <div key={`${result.index}-${result.sourceUrl}`}>
                    <span className={result.status === "rejected" ? "text-red-600" : result.status === "duplicate" ? "text-amber-700" : "text-emerald-700"}>{result.status}</span>
                    <strong>{result.vehicle || result.message || "Duplicate link skipped"}</strong>
                    {result.askingPrice ? <small>{money(result.askingPrice)}</small> : null}
                    {result.missingDetails?.length ? <small>Needs {result.missingDetails.join(", ")} before it can be used in an estimate.</small> : null}
                  </div>
                ))}
              </div>
            ) : null}
            <details className={ClassName.jijiSingleReview}>
              <summary>Review and correct one link before saving</summary>
              <form className={ClassName.jijiUrlRow} onSubmit={previewJiji}>
                <input className={ClassName.input} type="url" value={jijiUrl} onChange={(event) => setJijiUrl(event.target.value)} placeholder="https://jiji.com.et/.../cars/...html" required />
                <button className={ClassName.secondaryButton} disabled={savingId === "jiji-preview"}>{savingId === "jiji-preview" ? "Reading..." : "Read price"}</button>
              </form>
            {jijiRecord ? (
              <div className={ClassName.jijiPreview}>
                <div className={ClassName.jijiPreviewTop}>
                  <div><span>Review before saving</span><strong>{jijiRecord.title}</strong><small>{jijiRecord.conditionLabel || "Used car"} / observed today</small></div>
                  <strong>{money(jijiRecord.askingPrice)}</strong>
                </div>
                <div className={ClassName.formGrid}>
                  {([
                    ["makeName", "Make"],
                    ["model", "Model"],
                    ["year", "Year"],
                    ["mileage", "Mileage"],
                    ["fuelType", "Fuel"],
                    ["transmissionType", "Transmission"],
                    ["location", "Location"],
                    ["askingPrice", "Advertised price"],
                  ] as const).map(([field, label]) => (
                    <label className={ClassName.field} key={field}>
                      <span>{label}</span>
                      <input
                        className={ClassName.input}
                        inputMode={["year", "mileage", "askingPrice"].includes(field) ? "numeric" : undefined}
                        value={String(jijiRecord[field] || "")}
                        onChange={(event) => updateJijiRecord(field, event.target.value)}
                      />
                    </label>
                  ))}
                </div>
                <label className={ClassName.check}><input type="checkbox" checked={jijiRightsConfirmed} onChange={(event) => setJijiRightsConfirmed(event.target.checked)} /> Confirm this public URL may be used as internal price evidence</label>
                <button type="button" className={ClassName.primaryButton} disabled={!jijiRightsConfirmed || savingId === "jiji-save"} onClick={saveJijiObservation}>{savingId === "jiji-save" ? "Saving..." : "Save Jiji price"}</button>
              </div>
            ) : null}
            </details>
          </section>

          <form className={ClassName.observationForm} onSubmit={addObservation}>
            <div className={ClassName.sectionHeading}>
              <div><h2>Add a verified market observation</h2><p>Final selling prices are the strongest evidence. Asking prices are kept separate.</p></div>
            </div>
            <div className={ClassName.formGrid}>
              {(["makeName", "model", "year", "mileage", "fuelType", "transmissionType", "location", "askingPrice", "finalSalePrice", "sourceProvider", "observedAt"] as const).map((field) => (
                <label className={ClassName.field} key={field}>
                  <span>{field.replace(/([A-Z])/g, " $1")}</span>
                  <input
                    type={field === "observedAt" ? "date" : field === "year" || field === "mileage" || field === "askingPrice" || field === "finalSalePrice" ? "number" : "text"}
                    className={ClassName.input}
                    value={String(observation[field])}
                    onChange={(event) => setObservation((current) => ({ ...current, [field]: event.target.value }))}
                    required={field === "makeName" || field === "model" || field === "year"}
                  />
                </label>
              ))}
              <label className={ClassName.field}><span>Source type</span><select className={ClassName.input} value={observation.sourceType} onChange={(event) => setObservation((current) => ({ ...current, sourceType: event.target.value }))}><option>CompletedSale</option><option>DealerQuote</option><option>Manual</option><option>ExternalListing</option><option>BossCarListing</option></select></label>
              <label className={ClassName.check}><input type="checkbox" checked={observation.verified} onChange={(event) => setObservation((current) => ({ ...current, verified: event.target.checked }))} /> Verified evidence</label>
            </div>
            <button className={ClassName.primaryButton} disabled={savingId === "observation"}>{savingId === "observation" ? "Saving..." : "Save observation"}</button>
          </form>
          <details className={ClassName.datasetImporter}>
            <summary>Import an authorized market dataset</summary>
            <form onSubmit={importDataset}>
              <p>Use licensed/API/partner data only. Records improve private valuation evidence and are not published as BossCar listings.</p>
              <input className={ClassName.input} value={sourceProvider} onChange={(event) => setSourceProvider(event.target.value)} placeholder="Source provider, for example Mekina or Gari" required />
              <textarea className={ClassName.datasetText} value={datasetText} onChange={(event) => setDatasetText(event.target.value)} placeholder='[{"sourceId":"123","makeName":"Toyota","model":"Corolla","year":2018,"mileage":94000,"price":3400000,"publishedAt":"2026-08-12"}]' required />
              <label className={ClassName.check}><input type="checkbox" checked={rightsConfirmed} onChange={(event) => setRightsConfirmed(event.target.checked)} required /> BossCar has permission to use this source data for valuation</label>
              <button className={ClassName.primaryButton} disabled={savingId === "dataset"}>{savingId === "dataset" ? "Importing..." : "Import dataset"}</button>
            </form>
          </details>
          </>
        ) : null}

        {loading ? <div className={ClassName.empty}>Loading...</div> : tab === "requests" ? (
          <section className={ClassName.list}>
            {valuations.map((valuation) => {
              const draft = drafts[valuation._id] || draftFor(valuation);
              return (
                <article className={ClassName.card} key={valuation._id}>
                  <div className={ClassName.cardTop}>
                    <div><span className={ClassName.status}>{valuation.reviewStatus}</span><h2>{valuation.year} {valuation.makeName} {valuation.model}</h2><p>{valuation.mileage?.toLocaleString() || "Mileage unknown"} km · {valuation.fuelType || "Fuel unknown"} · {valuation.condition}</p></div>
                    <div className={ClassName.contact}><strong>{valuation.requester?.name || "Guest request"}</strong><span>{valuation.contactPhone || valuation.requester?.phoneNumber || "No phone"}</span><small>{new Date(valuation.createdAt).toLocaleString()}</small></div>
                  </div>
                  <div className={ClassName.resultGrid}>
                    <div><span>Likely sale</span><strong>{money(valuation.result.likelySalePrice)}</strong></div>
                    <div><span>Expected range</span><strong>{money(valuation.result.lowPrice)} - {money(valuation.result.highPrice)}</strong></div>
                    <div><span>Suggested asking</span><strong>{money(valuation.result.suggestedAskingPrice)}</strong></div>
                    <div><span>Evidence</span><strong>{valuation.result.askingPriceCount || 0} advertised / {valuation.result.completedSaleCount || 0} completed / {valuation.result.verifiedSaleCount || 0} verified</strong></div>
                  </div>
                  <p className={ClassName.method}>{valuation.result.confidence} confidence / quality {valuation.result.dataQualityScore ?? 0}/100 / {valuation.result.halfLifeDays}-day half-life / {valuation.result.calibrationSource === "verified_sales" ? "verified sale calibration" : valuation.result.calibrationSource === "completed_sales" ? "unverified completed-sale calibration" : "provisional calibration"}</p>
                  {valuation.result.detailMatch ? <p className={ClassName.method}>Detail match: {valuation.result.detailMatch.evidenceTier} / {valuation.result.detailMatch.exactDetailComparableCount} exact / {valuation.result.detailMatch.compatibleComparableCount} compatible / {valuation.result.detailMatch.rejectedComparableCount} rejected</p> : null}
                  {valuation.result.confidenceReasons?.length ? <p className={ClassName.method}>Review reasons: {valuation.result.confidenceReasons.join("; ")}</p> : null}
                  <div className={ClassName.reviewGrid}>
                    <input className={ClassName.input} type="number" value={draft.low} onChange={(event) => updateDraft(valuation._id, "low", event.target.value)} placeholder="Staff low" />
                    <input className={ClassName.input} type="number" value={draft.likely} onChange={(event) => updateDraft(valuation._id, "likely", event.target.value)} placeholder="Staff likely" />
                    <input className={ClassName.input} type="number" value={draft.high} onChange={(event) => updateDraft(valuation._id, "high", event.target.value)} placeholder="Staff high" />
                    <input className={ClassName.input} value={draft.notes} onChange={(event) => updateDraft(valuation._id, "notes", event.target.value)} placeholder="Review notes" />
                    <button className={ClassName.secondaryButton} disabled={savingId === valuation._id} onClick={() => saveReview(valuation._id)}>Save review</button>
                  </div>
                  <details className={ClassName.outcome}>
                    <summary>Record completed sale</summary>
                    <div className={ClassName.reviewGrid}>
                      <input className={ClassName.input} type="number" value={draft.asking} onChange={(event) => updateDraft(valuation._id, "asking", event.target.value)} placeholder="Original asking price" />
                      <input className={ClassName.input} type="number" value={draft.sold} onChange={(event) => updateDraft(valuation._id, "sold", event.target.value)} placeholder="Actual selling price" />
                      <input className={ClassName.input} type="date" value={draft.soldAt} onChange={(event) => updateDraft(valuation._id, "soldAt", event.target.value)} />
                      <label className={ClassName.check}><input type="checkbox" checked={draft.verified} onChange={(event) => updateDraft(valuation._id, "verified", event.target.checked)} /> Verified by BossCar</label>
                      <button className={ClassName.primaryButton} disabled={savingId === valuation._id} onClick={() => saveOutcome(valuation._id)}>Record sale</button>
                    </div>
                  </details>
                </article>
              );
            })}
            {!valuations.length ? <div className={ClassName.empty}>No valuation requests found.</div> : null}
          </section>
        ) : tab === "outcomes" ? (
          <section className={ClassName.list}>
            <div className={ClassName.outcomeGuide}>
              <div>
                <strong>Recover the final price for every completed listing.</strong>
                <span>A sold label alone cannot train or test the estimator. Call the owner, verify the agreed price, then record it on the car page.</span>
              </div>
              <span>{visibleMissingSaleOutcomes.length} need follow-up</span>
            </div>
            {visibleMissingSaleOutcomes.map((item) => (
              <article className={ClassName.missingSaleCard} key={item._id}>
                <div>
                  <span>Sold listing{item.tag ? ` / ${item.tag}` : ""}</span>
                  <h2>{item.year} {item.make?.name || "Unknown make"} {item.model}</h2>
                  <p>Original asking: {money(item.price)} / Owner: {item.owner?.name || "Unknown"}</p>
                  <small>{item.soldAt ? `Marked sold ${new Date(item.soldAt).toLocaleDateString()}` : "Sold date missing"}</small>
                </div>
                <div className={ClassName.missingSaleActions}>
                  <a href={`tel:${item.owner?.phoneNumber || item.phone || ""}`}>{item.owner?.phoneNumber || item.phone || "No phone"}</a>
                  <button className={ClassName.primaryButtonCompact} onClick={() => navigate(Paths.car.replace(":id", item._id))}>Record final price</button>
                </div>
              </article>
            ))}
            {!visibleMissingSaleOutcomes.length ? <div className={ClassName.empty}>No sold listings are missing a final price.</div> : null}
          </section>
        ) : tab === "market" ? (
          <section className={ClassName.tableCard}>
            <div className={ClassName.tableHeader}><span>Vehicle</span><span>Source</span><span>Asking</span><span>Final sale</span><span>Observed</span></div>
            {observations.map((item) => <div className={ClassName.tableRow} key={item._id}><strong>{item.year} {item.makeName} {item.model}</strong><span>{item.sourceProvider} / {item.sourceType}{item.verified ? " / Verified" : ""}</span><span>{money(item.askingPrice)}</span><span>{money(item.finalSalePrice)}</span><span>{new Date(item.lastObservedAt).toLocaleDateString()}</span></div>)}
            {!observations.length ? <div className={ClassName.empty}>No market observations found.</div> : null}
          </section>
        ) : (
          <section className={ClassName.list}>
            <div className={ClassName.modelGuide}>
              <strong>Review uncertain names before they affect valuations.</strong>
              <span>Approve a genuinely new model, merge a misspelling or reordered name into an approved model, or reject unusable data.</span>
            </div>
            {vehicleModels.filter((item) => item.status === "Pending").map((item) => {
              const availableTargets = vehicleModels.filter(
                (target) =>
                  target.status !== "Rejected" &&
                  target._id !== item._id &&
                  String(target.make?._id || "") === String(item.make?._id || "")
              );
              const selectedCandidate = item.reviewCandidates?.find(
                (candidate) => candidate.model._id === mergeTargets[item._id]
              );
              const targetOptions = [
                ...(item.reviewCandidates || []).map((candidate) => candidate.model),
                ...availableTargets.map((target) => ({
                  _id: target._id,
                  name: target.name,
                  normalizedName: target.normalizedName,
                  status: target.status as "Pending" | "Approved",
                })),
              ].filter(
                (target, index, options) =>
                  options.findIndex((candidate) => candidate._id === target._id) === index
              );
              const evidence = item.evidence;
              return (
                <article className={ClassName.modelCard} key={item._id}>
                  <div className={ClassName.modelIdentity}>
                    <span>{item.makeName}</span>
                    <h2>{item.name}</h2>
                    <small>
                      Seen {new Date(item.lastSeenAt).toLocaleDateString()}
                      {item.suggestedCanonicalModel
                        ? ` / Closest: ${item.suggestedCanonicalModel.name} (${Math.round((item.suggestedConfidence || 0) * 100)}%)`
                        : " / No safe suggestion"}
                    </small>
                    <p className={ClassName.modelEvidence}>
                      {evidence?.observationCount || 0} listings
                      {evidence?.minYear ? ` / ${evidence.minYear}${evidence.maxYear && evidence.maxYear !== evidence.minYear ? `-${evidence.maxYear}` : ""}` : ""}
                      {evidence?.medianPrice ? ` / median ${money(evidence.medianPrice)}` : ""}
                      {evidence?.fuels?.length ? ` / ${evidence.fuels.join(", ")}` : ""}
                    </p>
                    {selectedCandidate ? (
                      <p className={selectedCandidate.recommended ? ClassName.safeSuggestion : ClassName.cautionSuggestion}>
                        {selectedCandidate.recommended ? "Strong spelling/order match. Confirm only if these are the same model." : "Manual judgment required."}
                        {selectedCandidate.warnings.length ? ` ${selectedCandidate.warnings.join("; ")}.` : ""}
                      </p>
                    ) : mergeTargets[item._id] ? (
                      <p className={ClassName.cautionSuggestion}>No automated evidence match for this choice. Confirm the exact model manually before merging.</p>
                    ) : null}
                  </div>
                  <div className={ClassName.modelActions}>
                    <select
                      className={ClassName.input}
                      value={mergeTargets[item._id] || ""}
                      onChange={(event) =>
                        setMergeTargets((current) => ({ ...current, [item._id]: event.target.value }))
                      }
                    >
                      <option value="">Select same model</option>
                      {targetOptions.map((target) => (
                        <option value={target._id} key={target._id}>{target.name} ({target.status})</option>
                      ))}
                    </select>
                    <button className={ClassName.primaryButtonCompact} disabled={savingId === item._id} onClick={() => resolveModel(item, "approve")}>Approve as new</button>
                    <button className={ClassName.secondaryButton} disabled={savingId === item._id || !mergeTargets[item._id]} onClick={() => resolveModel(item, "merge")}>{selectedCandidate?.recommended ? "Confirm same model" : "Merge as alias"}</button>
                    <button className={ClassName.rejectButton} disabled={savingId === item._id} onClick={() => resolveModel(item, "reject")}>Reject</button>
                  </div>
                </article>
              );
            })}
            {!vehicleModels.some((item) => item.status === "Pending") ? (
              <div className={ClassName.empty}>No model names need review.</div>
            ) : null}
          </section>
        )}
      </main>
    </div>
  );
}

export default ValuationsPage;

const ClassName = {
  container: "flex flex-1 flex-col gap-4 pb-16 text-slate-950",
  header: "flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-end lg:justify-between",
  eyebrow: "text-[10px] font-bold tracking-[0.2em] text-primary",
  title: "mt-1 text-2xl font-bold tracking-tight sm:text-3xl",
  subtitle: "mt-1 max-w-2xl text-sm leading-5 text-slate-500",
  tabs: "grid grid-cols-2 rounded-xl bg-slate-100 p-1 sm:grid-cols-4",
  tab: "h-10 rounded-lg px-4 text-sm font-semibold text-slate-500",
  activeTab: "h-10 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm",
  toolbar: "grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-[minmax(0,1fr)_220px]",
  input: "h-11 min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-900",
  feedback: "rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700",
  coldStartNotice: "rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-900 [&>strong]:font-bold",
  calibrationCard: "grid grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[minmax(260px,1.5fr)_repeat(4,minmax(100px,1fr))] [&>div]:flex [&>div]:flex-col [&>div]:justify-center [&>div]:rounded-xl [&>div]:bg-slate-50 [&>div]:p-3 [&_span]:text-[10px] [&_span]:font-bold [&_span]:uppercase [&_span]:tracking-wide [&_span]:text-slate-400 [&_strong]:mt-1 [&_strong]:text-sm [&_small]:mt-1 [&_small]:text-xs [&_small]:leading-4 [&_small]:text-slate-500",
  coverageCard: "grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-2 xl:grid-cols-4 [&>div]:flex [&>div]:flex-col [&>div]:rounded-xl [&>div]:bg-slate-50 [&>div]:p-3 [&_span]:text-[10px] [&_span]:font-bold [&_span]:uppercase [&_span]:tracking-wide [&_span]:text-slate-400 [&_strong]:mt-1 [&_strong]:text-lg [&_small]:mt-1 [&_small]:text-xs [&_small]:leading-4 [&_small]:text-slate-500",
  trendCard: "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5",
  trendHeader: "flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between [&>div>span]:text-[10px] [&>div>span]:font-bold [&>div>span]:tracking-[0.16em] [&>div>span]:text-primary [&_h2]:mt-1 [&_h2]:text-xl [&_h2]:font-bold [&_p]:mt-1 [&_p]:max-w-2xl [&_p]:text-sm [&_p]:text-slate-500",
  trendFilters: "grid min-w-0 grid-cols-2 gap-2 sm:min-w-[300px]",
  trendSummary: "mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4 [&>div]:flex [&>div]:flex-col [&>div]:rounded-xl [&>div]:bg-slate-50 [&>div]:p-3 [&_span]:text-xs [&_span]:text-slate-500 [&_strong]:mt-1 [&_strong]:text-base [&_strong]:font-bold",
  trendLegend: "mt-4 flex flex-wrap gap-4 text-xs font-semibold text-slate-600 [&>span]:flex [&>span]:items-center [&>span]:gap-2 [&_i]:block [&_i]:h-2.5 [&_i]:w-2.5 [&_i]:rounded-full",
  trendEmpty: "mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500",
  jijiCard: "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm",
  jijiLabel: "mb-1 block text-[10px] font-bold tracking-[0.16em] text-primary",
  jijiUrlRow: "grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] [&_button]:h-11",
  jijiBatchText: "min-h-32 w-full resize-y rounded-xl border border-slate-200 bg-slate-50 p-3 font-mono text-xs leading-5 outline-none focus:border-slate-900",
  jijiBatchAction: "mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&_small]:max-w-2xl [&_small]:text-xs [&_small]:leading-5 [&_small]:text-slate-500",
  jijiBatchResults: "mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 [&>div]:flex [&>div]:min-w-0 [&>div]:flex-col [&>div]:rounded-xl [&>div]:bg-slate-50 [&>div]:p-3 [&_span]:text-[10px] [&_span]:font-bold [&_span]:uppercase [&_strong]:mt-1 [&_strong]:truncate [&_strong]:text-sm [&_small]:mt-1 [&_small]:text-xs [&_small]:text-slate-500",
  jijiSingleReview: "mt-5 border-t border-slate-200 pt-4 [&>summary]:cursor-pointer [&>summary]:text-sm [&>summary]:font-bold [&>form]:mt-3",
  jijiPreview: "mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4",
  jijiPreviewTop: "mb-4 flex flex-col gap-2 border-b border-slate-200 pb-4 sm:flex-row sm:items-start sm:justify-between [&>div]:flex [&>div]:flex-col [&>div>span]:text-[10px] [&>div>span]:font-bold [&>div>span]:uppercase [&>div>span]:tracking-wide [&>div>span]:text-primary [&>div>strong]:mt-1 [&>div>strong]:text-lg [&>div>small]:mt-1 [&>div>small]:text-xs [&>div>small]:text-slate-500 [&>strong]:text-lg [&>strong]:text-primary",
  observationForm: "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm",
  datasetImporter: "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm [&>summary]:cursor-pointer [&>summary]:font-bold [&_form]:mt-4 [&_form]:flex [&_form]:flex-col [&_form]:gap-3 [&_p]:text-sm [&_p]:text-slate-500",
  datasetText: "min-h-40 resize-y rounded-xl border border-slate-200 bg-white p-3 font-mono text-xs outline-none focus:border-slate-900",
  sectionHeading: "mb-4 [&_h2]:text-lg [&_h2]:font-bold [&_p]:mt-1 [&_p]:text-sm [&_p]:text-slate-500",
  formGrid: "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4",
  field: "flex min-w-0 flex-col gap-1.5 text-xs font-semibold capitalize text-slate-600",
  check: "flex min-h-11 items-center gap-2 text-sm font-semibold text-slate-600",
  primaryButton: "mt-4 h-11 rounded-xl bg-primary px-5 text-sm font-bold text-white disabled:opacity-50",
  secondaryButton: "h-11 rounded-xl bg-slate-950 px-4 text-sm font-bold text-white disabled:opacity-50",
  primaryButtonCompact: "h-11 rounded-xl bg-primary px-4 text-sm font-bold text-white disabled:opacity-50",
  rejectButton: "h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-600 disabled:opacity-50",
  list: "flex flex-col gap-4",
  card: "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm",
  cardTop: "flex flex-col gap-4 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between [&_h2]:mt-2 [&_h2]:text-xl [&_h2]:font-bold [&_p]:mt-1 [&_p]:text-sm [&_p]:text-slate-500",
  status: "rounded-full bg-red-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-primary",
  contact: "flex flex-col text-sm sm:items-end [&_span]:text-slate-600 [&_small]:mt-1 [&_small]:text-slate-400",
  resultGrid: "grid grid-cols-1 gap-3 py-4 sm:grid-cols-2 xl:grid-cols-4 [&>div]:flex [&>div]:flex-col [&>div]:rounded-xl [&>div]:bg-slate-50 [&>div]:p-3 [&_span]:text-xs [&_span]:text-slate-500 [&_strong]:mt-1 [&_strong]:text-sm",
  method: "text-xs font-medium text-slate-500",
  reviewGrid: "mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5",
  outcome: "mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 [&>summary]:cursor-pointer [&>summary]:text-sm [&>summary]:font-bold",
  empty: "rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500",
  tableCard: "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm",
  tableHeader: "hidden grid-cols-5 gap-3 bg-slate-950 px-4 py-3 text-xs font-bold uppercase tracking-wide text-white lg:grid",
  tableRow: "grid grid-cols-1 gap-1 border-t border-slate-100 px-4 py-4 text-sm first:border-0 lg:grid-cols-5 lg:gap-3 [&>span]:text-slate-600",
  modelGuide: "flex flex-col gap-1 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-950 [&>span]:text-blue-700",
  outcomeGuide: "flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 sm:flex-row sm:items-center sm:justify-between [&>div]:flex [&>div]:flex-col [&>div]:gap-1 [&_span]:text-amber-700",
  missingSaleCard: "flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between [&>div>span]:text-xs [&>div>span]:font-bold [&>div>span]:uppercase [&>div>span]:tracking-wide [&>div>span]:text-primary [&_h2]:mt-1 [&_h2]:text-lg [&_h2]:font-bold [&_p]:mt-1 [&_p]:text-sm [&_p]:text-slate-600 [&_small]:mt-1 [&_small]:block [&_small]:text-slate-400",
  missingSaleActions: "flex flex-col gap-2 sm:items-end [&_a]:text-sm [&_a]:font-bold [&_a]:text-slate-700",
  modelCard: "flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm xl:flex-row xl:items-center xl:justify-between",
  modelIdentity: "min-w-0 [&>span]:text-xs [&>span]:font-bold [&>span]:uppercase [&>span]:tracking-wide [&>span]:text-primary [&>h2]:mt-1 [&>h2]:text-xl [&>h2]:font-bold [&>small]:mt-1 [&>small]:block [&>small]:text-slate-500",
  modelEvidence: "mt-2 text-xs font-medium text-slate-600",
  safeSuggestion: "mt-2 max-w-2xl rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800",
  cautionSuggestion: "mt-2 max-w-2xl rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800",
  modelActions: "grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[240px_auto_auto_auto]",
};
